// opervex-frontend/src/components/LayoutConfigMenu.js

import { useState, useEffect } from "react";
import { Button, Modal, Form, ListGroup } from "react-bootstrap";
import { CiSettings } from "react-icons/ci";
import { Dropdown } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useAuth } from "../contexts/AuthContext";
import useOpervex from "../hooks/useOpervex";
import { SearchHelpModal } from "../utils/SearchHelp";
import ConfirmService from "../components/ConfirmationModal/ConfirmService";

const LayoutConfigMenu = ({ layout, setLayout, reportName, schema, onLayoutSelected }) => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [tempLayout, setTempLayout] = useState([]);
  const OpervexAPI = useOpervex();
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [header, setHeader] = useState({ variant: "", text: "" });

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLayout(schema);
      setTempLayout(schema);
      onLayoutSelected?.(null);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const handleResetLayout = async () => {
    setTempLayout(schema);
  };

  const saveLayoutUser = async (variantValue, textValue) => {
    try {
      const payload = {
        report: reportName,
        variant: variantValue,
        text: textValue,
        items: tempLayout.map((col, index) => ({
          seq: index + 1,
          field: col.key,
          label: col.label,
          type: col.type,
          visible: col.visible,
        })),
      };

      const result = await OpervexAPI.System.Layouts.save(payload);
      if (result.errors) {
        await ConfirmService.show("Aviso", result.errors, true);
        return;
      }
      setLayout(tempLayout);
      setHeader({ variant: variantValue, text: textValue });
      onLayoutSelected?.({ variant: variantValue, text: textValue });
      await ConfirmService.show("Layout", "Layout salvo com sucesso.", true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmSaveVariant = async () => {
    if (!header.variant) return;

    await saveLayoutUser(header.variant.toUpperCase(), header.text);

    setShowVariantModal(false);
  };

  const handleSaveLayout = async () => {
    setLayout(tempLayout);
    handleClose();
  };

  const handleToggleColumn = (key) => {
    setTempLayout(tempLayout.map((col) => (col.key === key ? { ...col, visible: !col.visible } : col)));
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedLayout = [...tempLayout];
    const [removed] = reorderedLayout.splice(result.source.index, 1);
    reorderedLayout.splice(result.destination.index, 0, removed);

    setTempLayout(reorderedLayout);
  };

  const handleSelectLayout = async () => {
    try {
      const item = await SearchHelpModal({
        urlData: "/System/Layouts/SearchHelp/" + reportName,
        schema: await OpervexAPI.System.Layouts.schema(),
      });

      if (!item?.variant) return;

      const savedLayout = await OpervexAPI.System.Layouts.findOne(reportName, item.variant);
      if (!savedLayout?.header || !Array.isArray(savedLayout.items) || !savedLayout.items.length) {
        await ConfirmService.show("Aviso", "O layout selecionado está vazio ou incompleto. Grave-o novamente.", true);
        return;
      }

      const selectedHeader = {
        variant: savedLayout.header.variant,
        text: savedLayout.header.text || "",
      };
      setHeader(selectedHeader);
      onLayoutSelected?.(selectedHeader);
      setLayout(savedLayout.items);
      setTempLayout(savedLayout.items);
    } catch (err) {
      console.error(err);
      await ConfirmService.show("Erro", "Não foi possível carregar o layout selecionado.", true);
    }
  };

  const handleModifyLayout = async () => {
    handleShow();
  };

  const handleSaveLayoutAs = async () => {
    setShowVariantModal(true);
  };

  const handleDeleteLayout = async () => {
    if (!header.variant) {
      await ConfirmService.show("Aviso", "Selecione um layout antes de eliminar.", true);
      return;
    }

    try {
      await OpervexAPI.System.Layouts.delete(reportName, header.variant);
      setHeader({ variant: "", text: "" });
      onLayoutSelected?.(null);
      setLayout(schema);
      setTempLayout(schema);
      await ConfirmService.show("Layout", "Layout eliminado com sucesso.", true);
    } catch (err) {
      console.error(err);
      await ConfirmService.show("Erro", "Não foi possível eliminar o layout.", true);
    }
  };

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="primary" size="sm">
          <CiSettings /> Layout
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item onClick={handleSelectLayout}>Selecionar</Dropdown.Item>
          <Dropdown.Item onClick={handleModifyLayout}>Modificar</Dropdown.Item>
          <Dropdown.Item onClick={handleSaveLayoutAs}>Gravar</Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={handleDeleteLayout} className="text-danger">
            Eliminar
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Layout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(provided) => (
                <ListGroup ref={provided.innerRef} {...provided.droppableProps}>
                  {tempLayout.map((col, index) => (
                    <Draggable key={col.key} draggableId={col.key} index={index}>
                      {(provided) => (
                        <ListGroup.Item ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <Form.Check
                            type="checkbox"
                            label={col.label}
                            checked={col.visible}
                            onChange={() => handleToggleColumn(col.key)}
                          />
                        </ListGroup.Item>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ListGroup>
              )}
            </Droppable>
          </DragDropContext>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleSaveLayout}>
            Salvar
          </Button>
          <Button variant="secondary" onClick={handleResetLayout}>
            Resetar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showVariantModal} onHide={() => setShowVariantModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Salvar Layout</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Variant</Form.Label>
            <Form.Control
              type="text"
              value={header.variant}
              onChange={(e) =>
                setHeader({
                  ...header,
                  variant: e.target.value,
                })
              }
              placeholder="Ex: DEFAULT"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              type="text"
              value={header.text}
              onChange={(e) =>
                setHeader({
                  ...header,
                  text: e.target.value,
                })
              }
              placeholder="Descrição do layout"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVariantModal(false)}>
            Cancelar
          </Button>

          <Button variant="success" onClick={handleConfirmSaveVariant}>
            Gravar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LayoutConfigMenu;
